import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  type ChangeEvent,
  type CSSProperties,
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@primer/react';
import { ImagePlus } from 'lucide-react';
import { useApp } from '../state/AppContext';
import { API_BASE } from '../state/client';
import { demoMemberMedia } from '../state/demoMedia';
import {
  gymCoverFileError,
  gymCoverUploadsAvailable,
  loadGymCover,
  resolveRemoteGymCover,
  selectGymCover,
  subscribeGymCover,
  updateGymCoverFraming,
  uploadGymCover,
  type GymCoverPhoto,
  type RemoteGymCover,
} from '../state/gymCover';
import {
  DEFAULT_GYM_COVER_CROP,
  GYM_COVER_ASPECT,
  clampGymCoverCrop,
  moveGymCoverCrop,
  type GymCoverCrop,
} from '../state/gymCoverCrop';
import { listGymGallery, notifyGymGalleryChange, type GalleryEntry } from '../state/gymGallery';
import { memberMediaImageUrl, normalizeMemberPhoto } from '../state/memberMediaUpload';
import { gymEstablishedKicker } from '../state/gymEstablished';
import { sampleImageSource, type SampleMemberMedia } from '../state/sampleMedia';
import { PhotoUploadMeter } from './PhotoUploadProgress';
import { SkeletonBlock } from './Skeleton';
import { Modal } from './UI';
import { bundledImage } from '../state/staticImages';
import './gymCoverPicker.css';

type GymBackdropTarget = {
  userId: string;
  sampleMedia?: SampleMemberMedia;
};

type GymBackdropContextValue = {
  active: boolean;
  setOverride: (value: GymBackdropTarget | null) => void;
};

const GymBackdropContext = createContext<GymBackdropContextValue>({
  active: false,
  setOverride: () => {},
});

/** Site shell backdrop: member cover by default, overridable on profile pages. */
export function GymBackdropProvider({ userId, children }: { userId: string; children: ReactNode }) {
  const [override, setOverride] = useState<GymBackdropTarget | null>(null);
  const value = useMemo(
    () => ({
      active: true,
      setOverride,
    }),
    [],
  );
  return (
    <GymBackdropContext.Provider value={value}>
      {children}
      <GymPageBackdrop userId={override?.userId ?? userId} sampleMedia={override?.sampleMedia} />
    </GymBackdropContext.Provider>
  );
}

/** Temporarily show another member's gym cover (clears on leave). */
export function useGymBackdropOverride(userId?: string, sampleMedia?: SampleMemberMedia) {
  const { active, setOverride } = useContext(GymBackdropContext);
  useEffect(() => {
    if (!active || !userId) return;
    setOverride({ userId, sampleMedia });
    return () => setOverride(null);
  }, [active, userId, sampleMedia, setOverride]);
  return active;
}

export function useGymCover(
  userId?: string,
  sampleMedia?: SampleMemberMedia,
): GymCoverPhoto | null {
  const { state, mode, session } = useApp();
  const [remote, setRemote] = useState<RemoteGymCover | null>(null);
  const [revision, setRevision] = useState(0);
  useEffect(() => subscribeGymCover(() => setRevision((value) => value + 1)), []);
  useEffect(() => {
    if (!userId || mode !== 'live') {
      setRemote(null);
      return;
    }
    let active = true;
    let objectUrl: string | null = null;
    const controller = new AbortController();
    void loadGymCover(
      API_BASE,
      userId,
      session?.access_token,
      controller.signal,
      fetch,
      String(revision),
    )
      .then((photo) => {
        if (!active || !photo) {
          if (active) setRemote(null);
          return;
        }
        objectUrl = URL.createObjectURL(photo.blob);
        setRemote({
          url: objectUrl,
          mediaId: photo.mediaId,
          framing: photo.framing,
          userId,
          token: session?.access_token,
          revision,
        });
      })
      .catch(() => {
        if (active) setRemote(null);
      });
    return () => {
      active = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [userId, mode, session?.access_token, revision]);
  const current = resolveRemoteGymCover({
    live: mode === 'live',
    userId,
    token: session?.access_token,
    revision,
    remote,
  });
  if (current) return current;
  if (!userId) return null;
  const sample =
    sampleMedia?.gym_cover_url ||
    state.members.find((member) => member.id === userId)?.sample_media?.gym_cover_url ||
    demoMemberMedia(userId)?.cover;
  return sample ? { url: sample } : null;
}
const DEFAULT_COVER = 'none';
function coverImage(cover: string | null) {
  if (!cover) return null;
  if (cover.startsWith('blob:')) return `url('${cover}')`;
  const sample = sampleImageSource(cover);
  if (sample) return `url('${sample}')`;
  const bundled = bundledImage(cover);
  if (bundled?.kind === 'gym' || bundled?.kind === 'hero') return `url('${bundled.src}')`;
  return null;
}

/** Same featured gym photo used on the profile hero and page backdrop. */
export function featuredGymCoverImage(cover: string | null) {
  return coverImage(cover) || DEFAULT_COVER;
}

/** The owner's gym title. Click it to rename; the name is stored on the gym. */
export function GymNameHeading() {
  const { currentUser, state, save, notify } = useApp();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(currentUser.gym_name);
  const [busy, setBusy] = useState(false);
  const skipBlur = useRef(false);
  useEffect(() => {
    if (!editing) setDraft(currentUser.gym_name);
  }, [currentUser.gym_name, editing]);

  async function commit(raw: string) {
    const next = raw.trim();
    if (!next) {
      notify('Give your gym a name.', 'error');
      setDraft(currentUser.gym_name);
      setEditing(false);
      return;
    }
    if (next === currentUser.gym_name) {
      setEditing(false);
      return;
    }
    setBusy(true);
    const saved = await save({
      members: state.members.map((member) =>
        member.id === currentUser.id ? { ...member, gym_name: next } : member,
      ),
    });
    setBusy(false);
    if (saved) {
      setEditing(false);
      notify('Gym name saved.');
    }
  }

  return (
    <h2>
      {editing ? (
        <form
          className="gym-name-form"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            skipBlur.current = true;
            void commit(draft);
          }}
        >
          <input
            aria-label="Gym name"
            value={draft}
            maxLength={100}
            autoFocus
            disabled={busy}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => {
              if (skipBlur.current) {
                skipBlur.current = false;
                return;
              }
              void commit(draft);
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Escape') return;
              skipBlur.current = true;
              setDraft(currentUser.gym_name);
              setEditing(false);
            }}
          />
        </form>
      ) : (
        <button
          type="button"
          className="gym-name-button"
          aria-label={`Rename ${currentUser.gym_name}`}
          onClick={() => setEditing(true)}
        >
          {currentUser.gym_name}
        </button>
      )}
    </h2>
  );
}

function backgroundImageUrl(value: string) {
  if (!value || value === 'none') return null;
  const match = /^url\(\s*(['"]?)(.*?)\1\s*\)$/i.exec(value.trim());
  return match?.[2] || null;
}

/** Blurry feature image behind app pages when a member gym cover is available. */
export function GymPageBackdrop({
  userId,
  sampleMedia,
}: {
  userId?: string;
  sampleMedia?: SampleMemberMedia;
}) {
  const cover = useGymCover(userId, sampleMedia);
  const image = featuredGymCoverImage(cover?.url ?? null);
  const coverUrl = backgroundImageUrl(image);
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const main = document.querySelector('.app-main, .public-profile-shell');
    if (!(main instanceof HTMLElement)) return;
    setHost(main);
    return () => {
      main.classList.remove('has-gym-backdrop');
      setHost(null);
    };
  }, []);
  useEffect(() => {
    if (!host) return;
    if (coverUrl) host.classList.add('has-gym-backdrop');
    else host.classList.remove('has-gym-backdrop');
  }, [host, coverUrl]);
  useEffect(() => {
    setReady(false);
    if (!coverUrl) return;
    let cancelled = false;
    const img = new Image();
    const markReady = () => {
      if (!cancelled) setReady(true);
    };
    img.addEventListener('load', markReady);
    img.decoding = 'async';
    img.src = coverUrl;
    if (img.complete && img.naturalWidth > 0) markReady();
    return () => {
      cancelled = true;
      img.removeEventListener('load', markReady);
    };
  }, [coverUrl]);
  if (!host || !coverUrl) return null;
  return createPortal(
    <div className={`gym-page-backdrop${ready ? ' is-ready' : ''}`} aria-hidden="true">
      <span style={{ '--gym-cover': image } as CSSProperties} />
    </div>,
    host,
  );
}
export function GymCoverFrame({
  userId,
  sampleMedia,
  className,
  children,
}: {
  userId?: string;
  sampleMedia?: SampleMemberMedia;
  className: string;
  children?: ReactNode;
}) {
  const cover = useGymCover(userId, sampleMedia);
  const image = featuredGymCoverImage(cover?.url ?? null);
  const framed = image !== 'none' && cover?.framing;
  return (
    <div className={className} style={{ '--gym-cover': framed ? 'none' : image } as CSSProperties}>
      {framed && <GymCoverArtwork key={cover.url} url={cover.url} framing={framed} />}
      {children}
    </div>
  );
}

type Size = { width: number; height: number };
type Point = { x: number; y: number };
type HeroPreview = Size & { textTop?: number };

/** Scale the full photo to cover the viewport, then clamp its focal point at the edges. */
export function GymCoverArtwork({
  url,
  framing,
  onLoad,
  onError,
}: {
  url: string;
  framing: GymCoverCrop;
  onLoad?: (size: Size) => void;
  onError?: () => void;
}) {
  const [aspect, setAspect] = useState(0);
  return (
    <div className="gym-cover-artwork" aria-hidden="true">
      <img
        src={url}
        alt=""
        draggable={false}
        onLoad={({ currentTarget: { naturalWidth: width, naturalHeight: height } }) => {
          setAspect(width / height);
          onLoad?.({ width, height });
        }}
        onError={onError}
        style={
          {
            '--cover-source-aspect': aspect || 1,
            '--cover-x': framing.x,
            '--cover-y': framing.y,
            '--cover-zoom': framing.zoom,
            visibility: aspect ? undefined : 'hidden',
          } as CSSProperties
        }
      />
    </div>
  );
}

async function loadGalleryPhotoBlob(
  mediaId: string,
  token: string | undefined,
  signal: AbortSignal,
  variant: 'master' | 'thumbnail',
) {
  const response = await fetch(memberMediaImageUrl(API_BASE, mediaId, variant), {
    cache: 'no-store',
    signal,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) return null;
  const blob = await response.blob();
  return blob.size > 0 ? blob : null;
}

function GalleryThumbnail({ mediaId, token }: { mediaId: string; token: string }) {
  const [preview, setPreview] = useState<{ mediaId: string; token: string; src: string | null }>();
  const src = preview?.mediaId === mediaId && preview.token === token ? preview.src : undefined;
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | undefined;
    const show = (src: string | null) => {
      if (!controller.signal.aborted) setPreview({ mediaId, token, src });
    };
    const timeout = window.setTimeout(() => {
      show(null);
      controller.abort();
    }, 15_000);
    void loadGalleryPhotoBlob(mediaId, token, controller.signal, 'thumbnail')
      .then(async (blob) => {
        if (controller.signal.aborted) return;
        if (!blob) {
          show(null);
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        const image = new Image();
        image.src = objectUrl;
        await image.decode();
        show(objectUrl);
      })
      .catch(() => show(null))
      .finally(() => window.clearTimeout(timeout));
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaId, token]);

  if (src === undefined) return <SkeletonBlock className="gym-cover-picker-placeholder" />;
  if (src === null) return <span className="gym-cover-picker-tile-empty">Preview unavailable</span>;
  return <img src={src} alt="" />;
}

function GymCoverPositionStep({
  blob,
  initialFraming,
  hero,
  gymName,
  gymKicker,
  busy,
  error,
  onBack,
  onConfirm,
}: {
  blob: Blob;
  initialFraming: GymCoverCrop;
  hero: HeroPreview | null;
  gymName: string;
  gymKicker: string;
  busy: boolean;
  error: string;
  onBack: () => void;
  onConfirm: (crop: GymCoverCrop) => void;
}) {
  const [url, setUrl] = useState('');
  const [size, setSize] = useState<Size | null>(null);
  const [imageError, setImageError] = useState('');
  const [crop, setCrop] = useState(initialFraming);
  const [previewWidth, setPreviewWidth] = useState(0);
  const previewRef = useRef<HTMLDivElement>(null);
  const pointers = useRef(new Map<number, Point>());
  const aspect = hero ? hero.width / hero.height : GYM_COVER_ASPECT;
  useEffect(() => {
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  useEffect(() => {
    const frame = previewRef.current;
    if (!frame) return;
    const observer = new ResizeObserver(([entry]) => setPreviewWidth(entry.contentRect.width));
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  function change(adjust: (value: GymCoverCrop) => GymCoverCrop) {
    if (!busy && size)
      setCrop((value) => clampGymCoverCrop(size.width, size.height, adjust(value), aspect));
  }

  function move(event: ReactPointerEvent<HTMLDivElement>) {
    const previous = pointers.current.get(event.pointerId);
    if (!previous || busy || !size) return;
    const frame = event.currentTarget.getBoundingClientRect();
    if (!frame.width || !frame.height) return;
    const next = { x: event.clientX, y: event.clientY };
    const other = [...pointers.current].find(([id]) => id !== event.pointerId)?.[1];
    pointers.current.set(event.pointerId, next);
    const before = other && Math.hypot(previous.x - other.x, previous.y - other.y);
    const after = other && Math.hypot(next.x - other.x, next.y - other.y);
    const local = (point: Point) => ({
      x: (other ? (point.x + other.x) / 2 : point.x) - frame.left,
      y: (other ? (point.y + other.y) / 2 : point.y) - frame.top,
    });
    const from = local(previous);
    const to = local(next);
    setCrop((value) =>
      moveGymCoverCrop(size, frame, value, from, to, before && after ? after / before : 1),
    );
  }

  return (
    <div className="gym-cover-position">
      <p className="gym-cover-picker-hint">Drag to reposition. Pinch or use the slider to zoom.</p>
      <div
        ref={previewRef}
        className="gym-cover-position-frame"
        style={{ aspectRatio: aspect }}
        role="group"
        tabIndex={0}
        aria-label="Featured image preview. Use arrow keys to position the photo."
        onPointerDown={(event) => {
          if (busy || !size || event.button !== 0 || pointers.current.size >= 2) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
        }}
        onPointerMove={move}
        onPointerUp={(event) => pointers.current.delete(event.pointerId)}
        onPointerCancel={(event) => pointers.current.delete(event.pointerId)}
        onLostPointerCapture={(event) => pointers.current.delete(event.pointerId)}
        onKeyDown={(event) => {
          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
          event.preventDefault();
          if (!size || busy) return;
          const frame = event.currentTarget.getBoundingClientRect();
          const dx = event.key === 'ArrowLeft' ? -12 : event.key === 'ArrowRight' ? 12 : 0;
          const dy = event.key === 'ArrowUp' ? -12 : event.key === 'ArrowDown' ? 12 : 0;
          setCrop((value) =>
            moveGymCoverCrop(size, frame, value, { x: 0, y: 0 }, { x: dx, y: dy }),
          );
        }}
      >
        {url && (
          <GymCoverArtwork
            key={url}
            url={url}
            framing={crop}
            onLoad={setSize}
            onError={() =>
              setImageError('This photo could not be opened. Go back and choose another image.')
            }
          />
        )}
        <div
          className="gym-hero gym-cover-preview-copy"
          aria-hidden="true"
          style={
            hero && previewWidth
              ? {
                  width: hero.width,
                  height: hero.height,
                  transform: `scale(${previewWidth / hero.width})`,
                }
              : undefined
          }
        >
          <div
            className="gym-hero-overlay"
            style={
              hero?.textTop !== undefined
                ? { justifyContent: 'flex-start', paddingTop: hero.textTop }
                : undefined
            }
          >
            <span className="tiny-kicker">{gymKicker}</span>
            <h2>{gymName}</h2>
          </div>
        </div>
      </div>
      <label className="gym-cover-zoom">
        Zoom
        <input
          type="range"
          min="1"
          max="5"
          step="0.01"
          value={crop.zoom}
          disabled={busy || !size}
          onChange={(event) => {
            const zoom = Number(event.target.value);
            change((value) => ({ ...value, zoom }));
          }}
        />
      </label>
      {(error || imageError) && (
        <p role="alert" className="form-error">
          {error || imageError}
        </p>
      )}
      <div className="gym-cover-position-actions">
        <Button onClick={() => change(() => DEFAULT_GYM_COVER_CROP)} disabled={busy || !size}>
          Reset
        </Button>
        <Button onClick={onBack} disabled={busy}>
          Back
        </Button>
        <Button
          variant="primary"
          onClick={() => onConfirm(crop)}
          disabled={busy || !size || Boolean(imageError)}
        >
          {busy ? 'Saving…' : 'Save featured image'}
        </Button>
      </div>
    </div>
  );
}

type CoverDraft =
  | { kind: 'current'; blob: Blob; mediaId: string; framing?: GymCoverCrop }
  | { kind: 'gallery'; blob: Blob; mediaId: string }
  | { kind: 'upload'; blob: Blob; checkpoint: { mediaId?: string } };

export function GymCoverControls({
  userId,
  placement = 'inline',
}: {
  userId: string;
  placement?: 'inline' | 'corner';
}) {
  const { currentUser, notify, mode, session } = useApp();
  const cover = useGymCover(userId);
  const currentMediaId = cover?.mediaId;
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'pick' | 'position'>('pick');
  const [draft, setDraft] = useState<CoverDraft | null>(null);
  const [hero, setHero] = useState<HeroPreview | null>(null);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [photos, setPhotos] = useState<GalleryEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ label: string; percent: number } | null>(null);
  const [error, setError] = useState('');
  const [uploads, setUploads] = useState(false);
  useEffect(() => {
    if (mode !== 'live') {
      setUploads(false);
      return;
    }
    let active = true;
    void gymCoverUploadsAvailable(API_BASE)
      .then((value) => {
        if (active) setUploads(value);
      })
      .catch(() => {
        if (active) setUploads(false);
      });
    return () => {
      active = false;
    };
  }, [mode]);
  useEffect(() => {
    if (!open || mode !== 'live' || !session) {
      setPhotos([]);
      return;
    }
    let active = true;
    setGalleryLoading(true);
    void listGymGallery(API_BASE, userId, session.access_token)
      .then((entries) => {
        if (active) setPhotos(entries.filter((entry) => entry.kind === 'photo' && entry.mediaId));
      })
      .catch(() => {
        if (active) setPhotos([]);
      })
      .finally(() => {
        if (active) setGalleryLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, mode, session, userId]);
  function closePicker() {
    if (busy) return;
    setOpen(false);
    setStep('pick');
    setDraft(null);
    setError('');
    setProgress(null);
  }
  function beginPosition(selection: CoverDraft) {
    setDraft(selection);
    setStep('position');
    setError('');
  }
  async function chooseGalleryPhoto(mediaId: string) {
    if (!session || busy) return;
    setBusy(true);
    setError('');
    try {
      const blob = await loadGalleryPhotoBlob(
        mediaId,
        session.access_token,
        AbortSignal.timeout(15_000),
        'master',
      );
      if (!blob) throw new Error('That gallery photo could not be opened.');
      beginPosition(
        mediaId === currentMediaId
          ? { kind: 'current', blob, mediaId, framing: cover?.framing }
          : { kind: 'gallery', blob, mediaId },
      );
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'That gallery photo could not be opened.',
      );
    } finally {
      setBusy(false);
    }
  }
  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || busy || !uploads) return;
    setBusy(true);
    setError('');
    try {
      const problem = gymCoverFileError(file);
      if (problem) throw new Error(problem);
      // Use the same full-frame photo for preview and upload, including camera orientation.
      const blob = await normalizeMemberPhoto(file);
      beginPosition({ kind: 'upload', blob, checkpoint: {} });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'That photo could not be opened.');
    } finally {
      setBusy(false);
    }
  }
  async function saveCover(crop: GymCoverCrop) {
    if (!draft || !session || busy || mode !== 'live') return;
    setBusy(true);
    setError('');
    try {
      const token = session.access_token;
      if (draft.kind === 'current') {
        await updateGymCoverFraming(API_BASE, token, draft.mediaId, crop);
      } else if (draft.kind === 'gallery') {
        await selectGymCover(API_BASE, token, draft.mediaId, crop);
      } else {
        if (!uploads) throw new Error('Photo uploads are temporarily unavailable.');
        await uploadGymCover(
          API_BASE,
          token,
          draft.blob,
          crop,
          fetch,
          (event) => setProgress({ label: event.label, percent: event.percent }),
          draft.checkpoint,
        );
      }
      if (draft.kind !== 'current') notifyGymGalleryChange();
      notify(
        draft.kind === 'upload'
          ? 'Featured image saved and added to your gym gallery.'
          : draft.kind === 'current'
            ? 'Cover framing saved.'
            : 'Featured image saved.',
      );
      setOpen(false);
      setStep('pick');
      setDraft(null);
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : 'That image could not be saved.';
      setError(message);
    } finally {
      setProgress(null);
      setBusy(false);
    }
  }
  return (
    <div className={`hero-image-actions${placement === 'corner' ? ' corner' : ''}`}>
      <button
        className="text-link"
        type="button"
        disabled={busy || (!uploads && !currentMediaId) || !session || mode !== 'live'}
        onClick={(event) => {
          const frame = event.currentTarget.closest('.gym-hero');
          const bounds = frame?.getBoundingClientRect();
          const kicker = frame?.querySelector('.gym-hero-overlay .tiny-kicker');
          setHero(
            bounds?.width && bounds.height
              ? {
                  width: bounds.width,
                  height: bounds.height,
                  textTop: kicker ? kicker.getBoundingClientRect().top - bounds.top : undefined,
                }
              : null,
          );
          setError('');
          setStep('pick');
          setDraft(null);
          setGalleryLoading(true);
          setOpen(true);
        }}
      >
        {busy ? (
          progress ? (
            'Uploading…'
          ) : (
            'Saving…'
          )
        ) : (
          <>
            <ImagePlus size={14} aria-hidden="true" />
            {currentMediaId ? 'Change gym cover photo' : 'Add your gym cover photo'}
          </>
        )}
      </button>
      <input
        ref={inputRef}
        className="sr-only"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        aria-label="Upload a new gym image"
        onChange={(event) => void onFile(event)}
        disabled={busy || !uploads || !session || mode !== 'live'}
      />
      {progress && !open && <PhotoUploadMeter label={progress.label} percent={progress.percent} />}
      {open && (
        <Modal
          title={step === 'pick' ? 'Choose featured image' : 'Position featured image'}
          onClose={closePicker}
          wide
        >
          {step === 'pick' ? (
            <div className="gym-cover-picker">
              <p className="gym-cover-picker-hint">
                Choose a gym photo or upload a new one. The full photo stays in your gallery.
              </p>
              {currentMediaId && (
                <div>
                  <Button disabled={busy} onClick={() => void chooseGalleryPhoto(currentMediaId)}>
                    Adjust current framing
                  </Button>
                </div>
              )}
              {galleryLoading ? (
                <div className="gym-cover-picker-grid" role="status">
                  <span className="sr-only">Loading gym photos…</span>
                  {Array.from({ length: 4 }, (_, index) => (
                    <SkeletonBlock key={index} className="gym-cover-picker-placeholder" />
                  ))}
                </div>
              ) : session && photos.length ? (
                <div className="gym-cover-picker-grid" role="group" aria-label="Gym gallery photos">
                  {photos.map(({ id, mediaId, caption }) => {
                    if (!mediaId) return null;
                    const featured = mediaId === currentMediaId;
                    const name = caption || 'gallery photo';
                    return (
                      <button
                        key={id}
                        type="button"
                        className="gym-cover-picker-tile"
                        disabled={busy || (!featured && !uploads)}
                        aria-label={
                          featured
                            ? `Featured photo: adjust framing for ${name}`
                            : `Use ${name} as featured image`
                        }
                        onClick={() => void chooseGalleryPhoto(mediaId)}
                      >
                        <GalleryThumbnail mediaId={mediaId} token={session.access_token} />
                        {featured && (
                          <span className="gym-cover-picker-featured" aria-hidden="true">
                            Using as Featured
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : !currentMediaId ? (
                <p className="gym-cover-picker-hint">
                  No gym photos yet. Upload one to set your featured image and start the gallery.
                </p>
              ) : null}
              {!uploads && (
                <p className="gym-cover-picker-hint">
                  Replacing the cover is temporarily unavailable.
                </p>
              )}
              <div className="gym-cover-picker-upload">
                <Button
                  leadingVisual={ImagePlus}
                  disabled={busy || !uploads}
                  onClick={() => inputRef.current?.click()}
                >
                  Upload new photo
                </Button>
                <Button onClick={closePicker} disabled={busy}>
                  Cancel
                </Button>
              </div>
              {error && (
                <p role="alert" className="form-error">
                  {error}
                </p>
              )}
            </div>
          ) : draft ? (
            <>
              <GymCoverPositionStep
                blob={draft.blob}
                initialFraming={
                  (draft.kind === 'current' && draft.framing) || DEFAULT_GYM_COVER_CROP
                }
                hero={hero}
                gymName={currentUser.gym_name}
                gymKicker={gymEstablishedKicker(currentUser.established_year)}
                busy={busy}
                error={error}
                onBack={() => {
                  if (busy) return;
                  setStep('pick');
                  setDraft(null);
                  setError('');
                }}
                onConfirm={(crop) => void saveCover(crop)}
              />
              {progress && <PhotoUploadMeter label={progress.label} percent={progress.percent} />}
            </>
          ) : null}
        </Modal>
      )}
    </div>
  );
}
